import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthService } from "services/AuthService";
import {
  completeShopifyAccountLink,
  readShopifyOnboardingSession,
  shopifyOnboardingPath,
} from "services/ShopifyOnboardingSession";
import {
  completeWooAccountLink,
  readWooOnboardingSession,
  wooDashboardPath,
  wooOnboardingPath,
} from "services/WooCommerceOnboardingSession";
import jwt_decode from "jwt-decode";
import EmailVerificationCard from "./emailVerificationCard";
import { OtpType } from "enum/OtpType";
import { AuthData, useAuth } from "context/AuthProvider";
import { UserTypes } from "enum/UserTypes";
import {
  CUSTOM_API_KEY_NEXT,
  CUSTOM_API_KEY_ONCE,
} from "pages/Integrations/CustomApiKeyReveal";

const OTP_EMAIL_KEY = "zuluMerchantOtpEmail";
const MERCHANT_PORTAL_RETURN_KEY = "zuluMerchantPortalReturn";

function verificationFailureMessage(error: unknown): string {
  const raw =
    typeof error === "string"
      ? error
      : error && typeof error === "object" && "message" in error
        ? String((error as { message?: string }).message || "")
        : error && typeof error === "object" && "error" in error
          ? String((error as { error?: string }).error || "")
          : "";
  const message = raw.replace(/\s+/g, " ").trim();
  return message || "Could not verify that code. Try again.";
}

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, , , , refreshUser] = useAuth() as AuthData;
  const routeEmail =
    typeof location.state?.email === "string"
      ? location.state.email.trim().toLowerCase()
      : "";
  const merchantPortalReturn = Boolean(
    location.state?.merchantPortalReturn ||
      sessionStorage.getItem(MERCHANT_PORTAL_RETURN_KEY) === "1"
  );
  const otpDelivered = location.state?.otpDelivered !== false;

  const [email] = useState(() => {
    if (routeEmail) {
      sessionStorage.setItem(OTP_EMAIL_KEY, routeEmail);
      return routeEmail;
    }
    return (sessionStorage.getItem(OTP_EMAIL_KEY) || "").trim().toLowerCase();
  });
  const [error, setError] = useState(
    routeEmail || sessionStorage.getItem(OTP_EMAIL_KEY)
      ? ""
      : "We could not find the email for this code. Go back and sign in again."
  );
  const [notice, setNotice] = useState(() =>
    routeEmail && otpDelivered
      ? `We sent a 6-digit code to ${routeEmail}. It expires in 10 minutes.`
      : ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [connectOnly, setConnectOnly] = useState(false);
  const [customerRole, setCustomerRole] = useState("");
  const recoveryStarted = useRef(false);

  document.title = "User Verification | Zulu Courier ";

  useEffect(() => {
    if (merchantPortalReturn) {
      sessionStorage.setItem(MERCHANT_PORTAL_RETURN_KEY, "1");
    }
  }, [merchantPortalReturn]);

  // Only resend when registration reported mail delivery failed — avoids
  // invalidating a code that was already accepted by the mail provider.
  useEffect(() => {
    if (!email || otpDelivered || recoveryStarted.current) {
      return;
    }
    recoveryStarted.current = true;
    const recover = async () => {
      setResending(true);
      setError("");
      try {
        const res = await AuthService.resendOtp({
          email,
          otpType: OtpType.SIGNUP_OTP,
        });
        if (!res.success) {
          setError(
            verificationFailureMessage(res.error) ||
              "Could not send a verification code. Tap Resend code."
          );
          return;
        }
        setNotice(`A verification code was sent to ${email}.`);
      } catch (resendError) {
        setError(verificationFailureMessage(resendError));
      } finally {
        setResending(false);
      }
    };
    recover();
  }, [email, otpDelivered]);

  const finishMerchantLink = async (userRole: string) => {
    if (merchantPortalReturn) {
      sessionStorage.removeItem(OTP_EMAIL_KEY);
      sessionStorage.removeItem(MERCHANT_PORTAL_RETURN_KEY);
      // Restores a stashed Zulu admin session when present.
      AuthService.userLogout();
      await refreshUser();
      window.location.replace("/merchant");
      return;
    }

    const shopifySession = readShopifyOnboardingSession();
    if (shopifySession) {
      const shopifyLink = await completeShopifyAccountLink();
      if (!shopifyLink.linked) {
        setConnectOnly(true);
        setError(shopifyLink.error || "Could not connect this shop. Try again.");
        return;
      }
      sessionStorage.removeItem(OTP_EMAIL_KEY);
      // Merchant dashboard uses the shop session query — put admin back.
      if (AuthService.restoreStaffSession()) {
        await refreshUser();
      }
      navigate(shopifyOnboardingPath(shopifySession), { replace: true });
      return;
    }
    const wooSession = readWooOnboardingSession();
    if (wooSession) {
      const wooLink = await completeWooAccountLink();
      if (!wooLink.linked) {
        setConnectOnly(true);
        setError(wooLink.error || "Could not connect this shop. Try again.");
        return;
      }
      sessionStorage.removeItem(OTP_EMAIL_KEY);
      if (AuthService.restoreStaffSession()) {
        await refreshUser();
      }
      navigate(
        wooLink.linked ? wooDashboardPath(wooSession) : wooOnboardingPath(wooSession),
        { replace: true }
      );
      return;
    }
    sessionStorage.removeItem(OTP_EMAIL_KEY);
    navigate(`/${userRole}/place-orders`, { replace: true });
  };

  const handleSubmit = async (otp: string) => {
    const code = otp.replace(/\s+/g, "");
    setNotice("");
    if (connectOnly && customerRole) {
      setSubmitting(true);
      setError("");
      try {
        await finishMerchantLink(customerRole);
      } catch (linkError) {
        setError(verificationFailureMessage(linkError));
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!email) {
      setError("We could not find the email for this code. Go back and sign in again.");
      return;
    }
    if (!code) {
      setError("Enter the verification code.");
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await AuthService.emailOTPVerification({ email, otpCode: code });
      if (!res.success || !res.data) {
        setError(verificationFailureMessage(res.error));
        return;
      }
      // Custom verify may return { token, apiKey }; other flows still return a JWT string.
      const payload = res.data as string | { token?: string; apiKey?: string };
      const accessToken =
        typeof payload === "string" ? payload : String(payload.token || "");
      const issuedApiKey =
        typeof payload === "object" && payload && payload.apiKey
          ? String(payload.apiKey)
          : "";
      if (!accessToken) {
        setError(verificationFailureMessage("Verification succeeded but no session token was returned."));
        return;
      }
      AuthService.setToken(accessToken);
      const refreshedUser = await refreshUser();
      const data = jwt_decode<{ user_role?: string }>(accessToken);
      const userRole = refreshedUser?.role || data?.user_role;
      if (userRole !== UserTypes.BUSINESS_CUSTOMER && userRole !== UserTypes.PERSONAL_CUSTOMER) {
        setError("This account cannot connect a shop. Sign in with a Zulu customer account.");
        return;
      }
      setCustomerRole(userRole);

      // Custom store: show the one-time API key before Merchant Portal login.
      if (issuedApiKey) {
        sessionStorage.setItem(CUSTOM_API_KEY_ONCE, issuedApiKey);
        sessionStorage.setItem(
          CUSTOM_API_KEY_NEXT,
          merchantPortalReturn ? "merchant" : userRole
        );
        navigate("/integrations/custom/api-key", {
          replace: true,
          state: { apiKey: issuedApiKey },
        });
        return;
      }

      await finishMerchantLink(userRole);
    } catch (verifyError) {
      setError(verificationFailureMessage(verifyError));
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    setError("");
    setNotice("");
    if (!email) {
      setError("We could not find the email for this code. Go back and sign in again.");
      return;
    }
    setResending(true);
    try {
      const res = await AuthService.resendOtp({ email, otpType: OtpType.SIGNUP_OTP });
      if (!res.success) {
        setError(verificationFailureMessage(res.error));
        return;
      }
      setNotice(`A new code was sent to ${email}.`);
    } catch (resendError) {
      setError(verificationFailureMessage(resendError));
    } finally {
      setResending(false);
    }
  };

  return (
    <EmailVerificationCard
      email={email}
      error={error}
      notice={notice}
      submitting={submitting}
      resending={resending}
      submitLabel={connectOnly ? "Connect shop" : undefined}
      onSubmit={handleSubmit}
      onResendOtp={resendOtp}
    />
  );
};

export default EmailVerification;
